import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppVersionData } from "../../entry/server/app.ts";
import { appRepo } from "../../repo/app-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import PaginationView from "../../componrnts/pagination.tsx";
import { Pagination } from "../../lib/api.ts";
import { appOsLabel } from "./app-version-form-fields.tsx";

export default function AppVersionsList() {
  const [versions, setVersions] = useState<AppVersionData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const { setMessage } = useContext(AppContext);

  useEffect(() => {
    const fetchVersions = async () => {
      setLoading(true);
      try {
        const response = await appRepo.getAppVersions(page);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت لیست نسخه‌ها ناموفق بود." });
          setVersions([]);
          return;
        }
        setVersions(response.data);
        setPagination(response.pagination ?? null);
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        setVersions([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchVersions();
  }, [page, setMessage]);

  const onDelete = async (id: number) => {
    if (!window.confirm("این نسخه حذف شود؟")) return;

    try {
      const response = await appRepo.deleteAppVersion(id);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "حذف نسخه ناموفق بود." });
        return;
      }
      setVersions((prev) => prev.filter((item) => item.id !== id));
      setMessage({ type: "success", message: "نسخه با موفقیت حذف شد." });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <AdminPageTitle title="نسخه‌های اپ" />
        <Link
          to="/admin/app-versions/new"
          className="rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400"
        >
          ایجاد نسخه
        </Link>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست نسخه‌ها...
        </div>
      ) : !versions.length ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          نسخه‌ای یافت نشد.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="min-w-full divide-y divide-slate-800 text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">سیستم‌عامل</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">نسخه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">نام نسخه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">لینک</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">آپدیت اجباری</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {versions.map((item) => (
                  <tr key={item.id} className="transition hover:bg-slate-900">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{item.id}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-100">{appOsLabel(item.os)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{item.version}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{item.version_name}</td>
                    <td className="max-w-xs truncate px-4 py-3 text-slate-300" title={item.link}>
                      {item.link || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          item.is_force_update
                            ? "bg-amber-500/20 text-amber-300"
                            : "bg-slate-700/60 text-slate-300"
                        }`}
                      >
                        {item.is_force_update ? "بله" : "خیر"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/admin/app-versions/${item.id}/edit`}
                          className="inline-flex rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500"
                        >
                          ویرایش
                        </Link>
                        <button
                          type="button"
                          onClick={() => void onDelete(item.id)}
                          className="inline-flex rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:border-rose-600"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}
    </section>
  );
}
